import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';

import { AboutPage } from './components/about/AboutPage';
import { FormEmbed } from './components/hero/FormEmbed';
import { Hero } from './components/hero/Hero';
import { LogoMarquee } from './components/hero/LogoMarquee';
import { Navbar } from './components/hero/Navbar';
import { StatsSection } from './components/hero/StatsSection';
import { FounderStrip } from './components/middle/FounderStrip';
import { ProblemSection } from './components/middle/ProblemSection';
import { ProcessSection } from './components/middle/ProcessSection';
import { SolutionSection } from './components/middle/SolutionSection';
import { ComparisonSection } from './components/end/ComparisonSection';
import { CtaSection } from './components/end/CtaSection';
import { FAQSection } from './components/end/FAQSection';
import { FeaturesSection } from './components/end/FeaturesSection';
import { Footer } from './components/end/Footer';
import { PricingSection } from './components/end/PricingSection';
import { ProofSection } from './components/end/ProofSection';
import { ROICalculator } from './components/end/ROICalculator';
import { SystemStatus } from './components/end/SystemStatus';
import { TwoTrackEconomy } from './components/end/TwoTrackEconomy';

import { initParticleCanvas, type ParticleCanvas } from './canvas/particles';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { EASE } from './tokens';
import type { View } from './types';

/** The bottom CTA section holds the embedded form. Every CTA points at it. */
const CTA_HREF = '#cta';

const PATHS: Record<View, string> = { home: '/', about: '/about' };

const viewForPath = (path: string): View => (path === PATHS.about ? 'about' : 'home');

export function App() {
  const [view, setView] = useState<View>(() => viewForPath(window.location.pathname));
  const mainRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<ParticleCanvas | null>(null);
  const pendingFormScroll = useRef(false);
  const reduced = usePrefersReducedMotion();

  const navigate = useCallback(
    (next: View): void => {
      setView(next);
      if (window.location.pathname !== PATHS[next]) {
        window.history.pushState({ view: next }, '', PATHS[next]);
      }
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    },
    [reduced],
  );

  const goHome = useCallback((): void => navigate('home'), [navigate]);
  const goAbout = useCallback((): void => navigate('about'), [navigate]);

  const scrollToForm = useCallback((): void => {
    document.getElementById('cta')?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
  }, [reduced]);

  /**
   * Every CTA scrolls to the embedded form rather than navigating to #cta, so
   * the URL never gains a hash and no history entry is pushed. From the about
   * view the section is not mounted, so navigate home first and scroll once it
   * has rendered.
   */
  const goToForm = useCallback(
    (event: MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();

      if (view !== 'home') {
        pendingFormScroll.current = true;
        navigate('home');
        return;
      }

      scrollToForm();
    },
    [view, navigate, scrollToForm],
  );

  // Deferred scroll to the form, after a CTA clicked from the about view.
  useEffect(() => {
    if (view !== 'home' || !pendingFormScroll.current) return;
    pendingFormScroll.current = false;

    let timer = 0;
    const raf = requestAnimationFrame(() => {
      timer = window.setTimeout(scrollToForm, 80);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [view, scrollToForm]);

  // Browser back/forward.
  useEffect(() => {
    const onPop = (): void => setView(viewForPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Own the particle canvas for the lifetime of the app.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const instance = initParticleCanvas(canvas);
    particlesRef.current = instance;

    return () => {
      instance.destroy();
      particlesRef.current = null;
    };
  }, []);

  // The two views use different particle palettes and densities.
  useEffect(() => {
    particlesRef.current?.setView(view);
  }, [view]);

  // Fade the new view in.
  useEffect(() => {
    const el = mainRef.current;
    if (!el || reduced) return;

    el.style.transition = 'none';
    el.style.opacity = '0';
    el.style.transform = 'translateY(12px)';

    const raf = requestAnimationFrame(() => {
      el.style.transition = `opacity 0.45s ${EASE}, transform 0.45s ${EASE}`;
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });

    return () => cancelAnimationFrame(raf);
  }, [view, reduced]);

  return (
    <>
      <canvas
        ref={canvasRef}
        id="ripple-bg"
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
      />

      <Navbar ctaHref={CTA_HREF} onCta={goToForm} onAbout={goAbout} onHome={goHome} currentView={view} />

      <div ref={mainRef} style={{ position: 'relative', zIndex: 1 }}>
        {view === 'home' ? (
          <main>
            <Hero ctaHref={CTA_HREF} onCta={goToForm} />
            <StatsSection />
            <LogoMarquee />
            <div style={{ padding: '0 20px 80px', position: 'relative', zIndex: 1 }}>
              <FormEmbed />
            </div>
            <ProblemSection />
            <FounderStrip onAbout={goAbout} />
            <SolutionSection />
            <ProcessSection ctaHref={CTA_HREF} onCta={goToForm} />
            <ProofSection />
            <FeaturesSection />
            <ROICalculator />
            <ComparisonSection />
            <PricingSection ctaHref={CTA_HREF} onCta={goToForm} />
            <FAQSection />
            <TwoTrackEconomy />
            <CtaSection />
            <Footer onAbout={goAbout} onHome={goHome} currentView={view} />
            <SystemStatus />
          </main>
        ) : (
          <main>
            <AboutPage ctaHref={CTA_HREF} onCta={goToForm} onBack={goHome} />
            <Footer onAbout={goAbout} onHome={goHome} currentView={view} />
            <SystemStatus />
          </main>
        )}
      </div>
    </>
  );
}
